/*!
 * AMOUR amour.si — cart-line "inspired-by" property fixup.
 *
 * Companion to assets/inspired-cart-property.js — that script can only
 * inject the property when the customer adds-to-cart from the product
 * detail page (because it relies on `window.amourInspiredProductData`
 * which is rendered by `snippets/inspired-cart-property.liquid` inside
 * the product form). All other add-to-cart surfaces leak through:
 *
 *   - predictive search "Add" buttons,
 *   - collection / homepage quick-add buttons,
 *   - sticky add-to-cart bar,
 *   - cart drawer cross-sells / Qikify upsells,
 *   - any third-party app dispatching its own /cart/add request.
 *
 * The result is a partially-tagged cart, exactly what we observed on
 * order #SI2317 — some lines have "Parfum, navdihnjen po: <fragrance>"
 * attached, others don't, even though every product has the metafields
 * populated.
 *
 * This file closes the loop by retroactively reconciling the cart:
 *
 *   1. On boot (DOMContentLoaded) and after every successful /cart/add,
 *      we GET /cart.js and look for line items missing the property.
 *   2. For each missing handle we GET /products/<handle>?view=cartdata
 *      to find out whether that product *should* have an inspired-by
 *      attribution (the `has_inspired` flag) and what the value is
 *      (the `fragrance` field). Results are cached in-memory per session.
 *   3. We POST /cart/change.js once per missing line, sending the union
 *      of existing properties + the new one (the change endpoint
 *      replaces all properties, so we have to re-send them).
 *   4. The whole pass is debounced and serialised so concurrent triggers
 *      collapse into a single round-trip and so we never race ourselves
 *      while line keys are being rewritten by Shopify.
 *
 * Safety:
 *   - Bails out early if no items, no missing items, or fragrance is
 *     blank for a given handle.
 *   - Marks every change request with `X-Amour-Fixup: 1` and ignores
 *     /cart/change in the trigger logic to prevent self-triggering.
 *   - Wrapped in try/catch and `.catch(noop)` so a broken response can
 *     never throw into the page or block real customer interactions.
 *   - Skips on /checkouts/* paths; the cart is frozen there anyway and
 *     mutating it mid-checkout is asking for trouble.
 */
(function () {
  'use strict';
  if (window.__amourInspiredCartFixupHooked) return;
  window.__amourInspiredCartFixupHooked = true;

  // The localized label is read from cartdata (`product.cartdata.liquid`
  // returns `"label"` resolved against the current request's locale), so
  // this script works on amour.si (sl) and on the multi-locale
  // amourparfums.* family (en/sl/de/hr/hu/it). We retain a small list of
  // historical hardcoded fallbacks only to recognize properties that were
  // injected by older builds before the cartdata-driven label rolled out.
  var LEGACY_LABELS = [
    'Parfum, navdihnjen po',
    'Perfume inspired by',
    'Parfüm inspiriert von',
    'Parfem nadahnut s',
    'Parfüm ihletője',
    'Profumo ispirato a'
  ];

  // We trigger fixup only after /cart/add. /cart/change is ignored to
  // avoid recursing into ourselves; /cart/update can also rewrite
  // properties so we treat it the same as add.
  var TRIGGER_RE = /\/cart\/(add|update)(\.js)?(\?|$)/i;

  // Skip work entirely on these paths — the cart is locked / not relevant.
  function isSkipPath() {
    var p = (window.location && window.location.pathname) || '';
    return /^\/checkouts?\//i.test(p) || /^\/orders\//i.test(p);
  }

  var inFlight = false;
  var pending = false;
  var cartdataCache = Object.create(null);

  function noop() { /* swallow */ }

  function fetchJson(url, opts) {
    var o = Object.assign({ credentials: 'same-origin', headers: { Accept: 'application/json' } }, opts || {});
    return fetch(url, o).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (txt) {
      try { return JSON.parse((txt || '').trim()); } catch (_) { return null; }
    });
  }

  // Locale-aware route prefix. /cart.js and /cart/change.js are locale
  // agnostic — they read/write the visitor's session cart regardless of
  // current locale — but `/products/{handle}?view=cartdata` IS locale
  // aware: fetching it without a prefix on a path-prefixed locale page
  // (e.g. amourparfums.com/hu) returns the default-locale render, which
  // means cartdata.label comes back in English even when the visitor's
  // cart should show "Parfüm ihletője:". `Shopify.routes.root` ends
  // with "/" and is "/hu/" on /hu, "/de/" on /de, "/" on default —
  // exactly the prefix we need.
  function localizeRoute(path) {
    var root = '/';
    try {
      if (window.Shopify && Shopify.routes && Shopify.routes.root) {
        root = Shopify.routes.root;
      }
    } catch (_) { /* never throw out of helper */ }
    if (path.charAt(0) === '/') path = path.slice(1);
    if (root.charAt(root.length - 1) !== '/') root = root + '/';
    return root + path;
  }

  function fetchCart() {
    return fetchJson('/cart.js');
  }

  function fetchCartdata(handle) {
    if (Object.prototype.hasOwnProperty.call(cartdataCache, handle)) {
      return Promise.resolve(cartdataCache[handle]);
    }
    var url = localizeRoute('/products/' + encodeURIComponent(handle) + '?view=cartdata');
    return fetchJson(url)
      .then(function (data) { cartdataCache[handle] = data; return data; })
      .catch(function () { cartdataCache[handle] = null; return null; });
  }

  // The property key written by inspired-cart-property.liquid is the
  // localized label *with the trailing colon stripped* (the snippet does
  // `| replace: ':', ''`). product.cartdata.liquid does NOT strip the
  // colon because cartdata.label is also displayed verbatim in the
  // Qikify drawer subtitle, where the colon is part of the visual.
  // Normalize here so property-key comparison + write match the snippet.
  function stripColon(s) {
    return String(s || '').replace(/:\s*$/, '').trim();
  }

  function lineNeedsFixup(item, cartdata) {
    if (!item || !item.handle) return false;
    if (!cartdata || !cartdata.fragrance) return false;
    var p = item.properties || {};
    var fragLc = String(cartdata.fragrance || '').trim().toLowerCase();
    if (!fragLc) return false;
    // Skip if the locale-specific label already carries a non-blank value.
    var localKey = stripColon(cartdata.label);
    if (localKey) {
      var current = p[localKey];
      if (current && String(current).length > 0) return false;
    }
    // Skip if a legacy build wrote the property under a sibling-locale label.
    for (var i = 0; i < LEGACY_LABELS.length; i++) {
      var lv = p[LEGACY_LABELS[i]];
      if (lv && String(lv).trim().toLowerCase() === fragLc) return false;
    }
    // Last-ditch: skip if any existing property value already matches the
    // fragrance string verbatim (e.g. apps may write under a custom label).
    for (var k in p) {
      if (Object.prototype.hasOwnProperty.call(p, k)) {
        if (String(p[k] || '').trim().toLowerCase() === fragLc) return false;
      }
    }
    return true;
  }

  function changeLine(item, label, fragrance) {
    var key = stripColon(label);
    if (!key) return Promise.resolve();
    var newProps = Object.assign({}, item.properties || {});
    newProps[key] = String(fragrance);
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Amour-Fixup': '1'
      },
      body: JSON.stringify({ id: item.key, properties: newProps })
    }).catch(noop);
  }

  function runFixupOnce() {
    if (isSkipPath()) return Promise.resolve();
    return fetchCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) return;

      // Fetch cartdata for ALL line items first so we know each product's
      // locale-aware label and fragrance before we decide which lines need
      // fixup. (We can't decide that without per-product cartdata since the
      // label string varies across locales / stores.)
      var uniqHandles = [];
      cart.items.forEach(function (it) {
        if (uniqHandles.indexOf(it.handle) === -1) uniqHandles.push(it.handle);
      });
      return Promise.all(uniqHandles.map(fetchCartdata)).then(function () {
        var missing = cart.items.filter(function (it) {
          return lineNeedsFixup(it, cartdataCache[it.handle]);
        });
        if (missing.length === 0) return;

        // Serialise change requests so we don't race ourselves while
        // Shopify rewrites line keys on the server side. After each
        // change we re-fetch /cart.js to get the freshest key for the
        // next item (line keys change when properties change).
        return missing.reduce(function (chain) {
          return chain.then(function () {
            return fetchCart().then(function (fresh) {
              if (!fresh || !Array.isArray(fresh.items)) return;
              // Find the next line that still needs fixup. We can't
              // trust the original `missing` array's keys because
              // earlier changes may have rewritten them.
              var next = null;
              var nextData = null;
              for (var i = 0; i < fresh.items.length; i++) {
                var d = cartdataCache[fresh.items[i].handle];
                if (lineNeedsFixup(fresh.items[i], d)) {
                  next = fresh.items[i];
                  nextData = d;
                  break;
                }
              }
              if (!next || !nextData) return;
              return changeLine(next, nextData.label, nextData.fragrance);
            });
          });
        }, Promise.resolve());
      });
    }).catch(noop);
  }

  function debounceRun() {
    if (inFlight) { pending = true; return; }
    inFlight = true;
    runFixupOnce().then(function () {
      inFlight = false;
      if (pending) { pending = false; setTimeout(debounceRun, 50); }
    }, function () {
      inFlight = false;
      if (pending) { pending = false; setTimeout(debounceRun, 50); }
    });
  }

  // ---------- triggers ----------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(debounceRun, 200); });
  } else {
    setTimeout(debounceRun, 200);
  }

  // Hook fetch — only for triggers, not body mutation. We never replace
  // the request, only schedule a fixup pass after it resolves.
  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && (input.url || input.href)) || '';
      // Suppress self-triggering: change requests we issued ourselves.
      var isSelf = false;
      try {
        if (init && init.headers) {
          var h = init.headers;
          if (typeof h.get === 'function') isSelf = !!h.get('X-Amour-Fixup');
          else if (typeof h === 'object') {
            for (var k in h) {
              if (Object.prototype.hasOwnProperty.call(h, k) && k.toLowerCase() === 'x-amour-fixup') { isSelf = true; break; }
            }
          }
        }
      } catch (_) {}
      var p = origFetch.apply(this, arguments);
      if (!isSelf && TRIGGER_RE.test(url)) {
        p.then(function () { setTimeout(debounceRun, 100); }, noop);
      }
      return p;
    };
  }

  // Hook XHR — same, trigger-only.
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var origOpen = XHR.prototype.open;
    var origSend = XHR.prototype.send;
    var origSetRequestHeader = XHR.prototype.setRequestHeader;
    XHR.prototype.open = function (method, url) {
      try { this.__amourFixupUrl = url || ''; } catch (_) {}
      return origOpen.apply(this, arguments);
    };
    XHR.prototype.setRequestHeader = function (name, value) {
      try {
        if (String(name).toLowerCase() === 'x-amour-fixup') this.__amourFixupSelf = true;
      } catch (_) {}
      return origSetRequestHeader.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      try {
        var url = this.__amourFixupUrl || '';
        if (!this.__amourFixupSelf && TRIGGER_RE.test(url)) {
          this.addEventListener('loadend', function () { setTimeout(debounceRun, 100); });
        }
      } catch (_) {}
      return origSend.apply(this, arguments);
    };
  }

  // Manual hook for app-driven flows that bypass fetch/XHR (rare, but
  // exposing this gives us a no-deploy escape hatch).
  window.amourCartFixup = { run: debounceRun };
})();
